import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Stack,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  Divider
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CallMade as OutboundIcon,
  CallReceived as InboundIcon,
  SwapHoriz as InternalIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Timer as TimerIcon,
  PhoneMissed as MissedIcon,
  CheckCircle as AnsweredIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { format, startOfDay, endOfDay } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { formatInUserTimezone } from '../../utils/dateTimeUtils';
import userPresenceService from '../../services/userPresence.service';

interface CallRecord {
  id: number;
  extension: string;
  phone_number?: string;
  incoming_did?: string;
  extension_name: string;
  direction: 'inbound' | 'outbound' | 'internal';
  remote_number: string;
  caller_id_name: string;
  start_time: string;
  end_time: string;
  duration: number;
  duration_formatted: string;
  disposition: string;
  rec_uniqueid?: string;
  wait_time?: number;
  is_transfer: boolean;
  transferred_from?: string;
  is_callback: boolean;
  area_code?: string;
  recording?: {
    id: number;
    size: number;
    storage: string;
    url: string;
  };
}

interface CallStatistics {
  total_calls: number;
  inbound_calls: number;
  outbound_calls: number;
  internal_calls: number;
  total_duration: number;
  total_duration_formatted: string;
  inbound_duration: number;
  inbound_duration_formatted: string;
  avg_inbound_duration: number;
  avg_inbound_duration_formatted: string;
  outbound_duration: number;
  outbound_duration_formatted: string;
  avg_outbound_duration: number;
  avg_outbound_duration_formatted: string;
  internal_duration: number;
  internal_duration_formatted: string;
  avg_internal_duration: number;
  avg_internal_duration_formatted: string;
  avg_duration: number;
  avg_duration_formatted: string;
  answered_calls: number;
  missed_calls: number;
  busy_calls: number;
  unique_numbers: number;
  answer_rate: number;
}

interface ActiveCall {
  id: number;
  custid: string;
  extension: string;
  phone_number?: string;
  incoming_did?: string;
  call_direction: string;
  remote_number: string;
  start_time: string;
  channel_id: string;
  caller_id_name: string;
  duration_seconds: number;
}



const UserCallHistory: React.FC = () => {
  const { member } = useAuth();
  const theme = useTheme();
  // Add noSsr option to prevent SSR/hydration issues
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  
  // Safe storage access helper for incognito mode
  const safeGetItem = (storage: Storage, key: string): string | null => {
    try {
      return storage.getItem(key);
    } catch (error) {
      console.error(`[UserCallHistory] Error accessing ${storage === localStorage ? 'localStorage' : 'sessionStorage'}:`, error);
      return null;
    }
  };

  const safeSetItem = (storage: Storage, key: string, value: string): void => {
    try {
      storage.setItem(key, value);
    } catch (error) {
      console.error(`[UserCallHistory] Error writing to ${storage === localStorage ? 'localStorage' : 'sessionStorage'}:`, error);
    }
  };

  // On mobile, ensure token is available in both storages
  useEffect(() => {
    if (isMobile) {
      const localToken = safeGetItem(localStorage, 'access_token');
      const sessionToken = safeGetItem(sessionStorage, 'access_token');
      const refreshToken = safeGetItem(localStorage, 'refresh_token') || safeGetItem(sessionStorage, 'refresh_token');

      if (localToken && !sessionToken) {
        console.log('[UserCallHistory] Copying tokens to sessionStorage for mobile');
        safeSetItem(sessionStorage, 'access_token', localToken);
        if (refreshToken) safeSetItem(sessionStorage, 'refresh_token', refreshToken);
      } else if (!localToken && sessionToken) {
        console.log('[UserCallHistory] Copying tokens to localStorage for mobile');
        safeSetItem(localStorage, 'access_token', sessionToken);
        if (refreshToken) safeSetItem(localStorage, 'refresh_token', refreshToken);
      }
    }
  }, [isMobile]);
  
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [activeCalls, setActiveCalls] = useState<ActiveCall[]>([]);
  const [statistics, setStatistics] = useState<CallStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  console.log('UserCallHistory rendering, member:', member);
  
  // Helper function to create a date in local timezone
  const createLocalDate = (dateString: string): Date | null => {
    try {
      if (!dateString || typeof dateString !== 'string') {
        return null;
      }
      const parts = dateString.split('-');
      if (parts.length !== 3) {
        return null;
      }
      const [year, month, day] = parts.map(Number);
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return null;
      }
      const date = new Date(year, month - 1, day);
      // Validate the date is valid
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    } catch (error) {
      console.error('[UserCallHistory] Error creating date:', error);
      return null;
    }
  };

  // Safe date change handlers with debouncing for mobile
  const handleStartDateChange = (value: string) => {
    try {
      const newDate = value ? createLocalDate(value) : null;
      setStartDate(newDate);

      // Clear existing timer
      if (dateChangeTimer.current) {
        clearTimeout(dateChangeTimer.current);
      }

      // Set new timer to fetch data after delay
      dateChangeTimer.current = setTimeout(() => {
        setPage(0); // Reset to first page
      }, 500); // Wait 500ms after user stops typing
    } catch (error) {
      console.error('[UserCallHistory] Error in handleStartDateChange:', error);
      setError('Invalid start date');
    }
  };

  const handleEndDateChange = (value: string) => {
    try {
      const newDate = value ? createLocalDate(value) : null;
      setEndDate(newDate);

      // Clear existing timer
      if (dateChangeTimer.current) {
        clearTimeout(dateChangeTimer.current);
      }

      // Set new timer to fetch data after delay
      dateChangeTimer.current = setTimeout(() => {
        setPage(0); // Reset to first page
      }, 500); // Wait 500ms after user stops typing
    } catch (error) {
      console.error('[UserCallHistory] Error in handleEndDateChange:', error);
      setError('Invalid end date');
    }
  };
  
  // Filters - Default to today
  const [startDate, setStartDate] = useState<Date | null>(startOfDay(new Date()));
  const [endDate, setEndDate] = useState<Date | null>(endOfDay(new Date()));
  const [direction, setDirection] = useState<'' | 'inbound' | 'outbound' | 'internal'>('');
  const [searchNumber, setSearchNumber] = useState('');
  
  // Debounce timer for date changes
  const dateChangeTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Auto-refresh
  const [autoRefresh] = useState(true);
  
  // Track playing recordings
  const [playingRecordingId, setPlayingRecordingId] = useState<number | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  
  // Prevent concurrent API calls
  const fetchingRef = useRef(false);

  const fetchCallHistory = useCallback(async () => {
    console.log('fetchCallHistory called');
    
    // Prevent concurrent requests
    if (fetchingRef.current) {
      console.log('[UserCallHistory] Skipping fetch - already in progress');
      return;
    }
    
    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      // Double-check token before making request
      const token = safeGetItem(localStorage, 'access_token') || safeGetItem(sessionStorage, 'access_token');
      if (!token) {
        console.warn('[UserCallHistory] No token found before API call!');
        setError('Authentication required. Please refresh the page.');
        return;
      }
      
      const params = {
        start_date: startDate && !isNaN(startDate.getTime()) ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate && !isNaN(endDate.getTime()) ? format(endDate, 'yyyy-MM-dd') : undefined,
        direction: direction || undefined,
        remote_number: searchNumber || undefined,
        page: page + 1,
        per_page: rowsPerPage
      };
      
      console.log('[UserCallHistory] Making API call with params:', params);
      const response = await userPresenceService.getUserCallHistory(params);
      
      console.log('Call history response:', response);
      if (response.success) {
        setCalls(response.data.calls);
        setStatistics(response.data.statistics);
        setTotalCount(response.data.pagination.total);
      } else {
        setError('Failed to load call history');
      }
    } catch (err: any) {
      console.error('Error fetching call history:', err);
      
      // Check if we still have a token before assuming session expired
      const hasToken = safeGetItem(localStorage, 'access_token') || safeGetItem(sessionStorage, 'access_token');
      
      if (err?.response?.status === 401) {
        if (hasToken) {
          // Token exists but might be expired, try to refresh
          console.log('[UserCallHistory] 401 error but token exists, showing error instead of logout');
          setError('Please wait, refreshing session...');
          // The API interceptor should handle token refresh
        } else {
          setError('Session expired. Please refresh the page.');
        }
      } else if (err?.response?.status === 403) {
        setError('Access denied. Please check your permissions.');
      } else if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        setError('Request timed out. Please try again.');
      } else {
        setError('Error loading call history. Please try again.');
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [startDate, endDate, direction, searchNumber, page, rowsPerPage]);

  const fetchActiveCalls = useCallback(async () => {
    try {
      const response = await userPresenceService.getUserActiveCalls();
      
      if (response.success) {
        const newActiveCalls = response.data.activeCalls || [];
        
        setActiveCalls(prevActiveCalls => {
          // Check if any calls ended
          if (prevActiveCalls.length > 0 && newActiveCalls.length < prevActiveCalls.length) {
            console.log('Call ended, refreshing call history...');
            setTimeout(() => {
              fetchCallHistory();
            }, 1000);
            // Check for recordings after multiple intervals
            setTimeout(() => fetchCallHistory(), 5000);
            setTimeout(() => fetchCallHistory(), 30000);
            setTimeout(() => fetchCallHistory(), 60000);
          }
          return newActiveCalls;
        });
      }
    } catch (err) {
      console.error('Error fetching active calls:', err);
    }
  }, [fetchCallHistory]);


  useEffect(() => {
    // Prevent initial fetch if component is not ready or user is not authenticated
    if (!member) {
      console.log('[UserCallHistory] Skipping fetch - no member data');
      return;
    }

    // Log token status before fetching
    const token = safeGetItem(localStorage, 'access_token') || safeGetItem(sessionStorage, 'access_token');
    console.log('[UserCallHistory] useEffect triggered, token status:', token ? 'Present' : 'Missing');
    console.log('[UserCallHistory] Date range:', startDate, 'to', endDate);

    // Add small delay on mobile to ensure token is properly set
    const isMobileDevice = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (isMobileDevice) {
      const timer = setTimeout(() => {
        fetchCallHistory();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      fetchCallHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, direction, searchNumber, page, rowsPerPage, member]);
  
  // Cleanup timer and audio on unmount
  useEffect(() => {
    return () => {
      if (dateChangeTimer.current) {
        clearTimeout(dateChangeTimer.current);
      }
      // Clean up audio on unmount
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Skip if no member data
    if (!member) {
      return;
    }
    
    fetchActiveCalls();
    
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchActiveCalls();
      }, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [fetchActiveCalls, autoRefresh, member]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchCallHistory();
  };

  const handlePlayRecording = async (call: CallRecord) => {
    if (!call.recording) return;

    // If this recording is already playing, stop it
    if (playingRecordingId === call.recording.id) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      setPlayingRecordingId(null);
      setAudioUrl(null);
      return;
    }

    // Stop any currently playing audio
    if (playingRecordingId) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    }

    try {
      let newAudioUrl: string;

      // Check if this is already a shareable link (starts with /r/)
      if (call.recording.url.includes('/r/')) {
        // Convert shareable URL to direct API URL for production compatibility
        const token = call.recording.url.split('/r/')[1];
        newAudioUrl = `/api/v1/endpoints/recording.php?token=${token}`;
      } else {
        // For API URLs, use them directly
        newAudioUrl = call.recording.url;
      }

      // Set the audio URL and recording ID - playback will happen automatically
      setAudioUrl(newAudioUrl);
      setPlayingRecordingId(call.recording.id);
    } catch (err) {
      console.error('Error playing recording:', err);
      setError('Failed to load recording');
      setPlayingRecordingId(null);
      setAudioUrl(null);
    }
  };

  const handleDownloadRecording = async (call: CallRecord) => {
    if (!call.recording) return;

    try {
      // Fetch the recording and create a blob URL for download
      const response = await fetch(call.recording.url, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recording');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `recording_${call.id}_${call.start_time.split(' ')[0]}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (err) {
      console.error('Error downloading recording:', err);
      setError('Failed to download recording');
    }
  };

  const handleCopyRecordingLink = async (call: CallRecord) => {
    if (!call.recording) return;
    
    try {
      await navigator.clipboard.writeText(call.recording.url);
      // You could show a success message here
    } catch (err) {
      console.error('Error copying link:', err);
      setError('Failed to copy recording link');
    }
  };

  const getDispositionColor = (disposition: string, duration?: number) => {
    const disp = disposition?.toUpperCase() || '';
    
    if (!disp) {
      return 'default';
    }
    
    if (disp === 'BUSY' && duration && duration > 20) {
      return 'info';
    }
    
    switch (disp) {
      case 'ANSWERED':
      case 'ANSWER':
        return 'success';
      case 'NO ANSWER':
      case 'NOANSWER':
        return 'warning';
      case 'BUSY':
        return 'error';
      case 'FAILED':
      case 'CHANUNAVAIL':
        return 'error';
      case 'CANCEL':
        return 'default';
      case 'CONGESTION':
        return 'error';
      default:
        return 'default';
    }
  };

  const getDisplayDisposition = (disposition: string, duration: number) => {
    const disp = disposition?.toUpperCase() || '';
    
    if (!disp || disp === 'UNKNOWN') {
      return 'IVR-Hangup';
    }
    
    if (disp === 'BUSY' && duration > 20) {
      return 'VM-No Message';
    }
    
    switch (disp) {
      case 'ANSWERED':
      case 'ANSWER':
        return 'Answered';
      case 'NOANSWER':
        return 'No Answer';
      case 'BUSY':
        return 'Busy';
      case 'CANCEL':
        return 'Cancelled';
      case 'CHANUNAVAIL':
        return 'Unavailable';
      case 'CONGESTION':
        return 'Network Busy';
      case 'FAILED':
        return 'Failed';
      default:
        return disposition;
    }
  };

  const getDirectionIcon = (direction: string) => {
    switch (direction) {
      case 'inbound':
        return <InboundIcon color="primary" fontSize="small" />;
      case 'outbound':
        return <OutboundIcon color="secondary" fontSize="small" />;
      case 'internal':
        return <InternalIcon color="info" fontSize="small" />;
      default:
        return <PhoneIcon fontSize="small" />;
    }
  };


  if (loading && !calls.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Active Calls Section */}
      {activeCalls.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: isMobile ? 'small' : 'medium' }} />
                {isMobile ? `Active (${activeCalls.length})` : `Your Active Calls (${activeCalls.length})`}
              </Typography>
            </Box>
            {isMobile ? (
              <List sx={{ pt: 0 }}>
                {activeCalls.map((call, index) => (
                  <React.Fragment key={call.id}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <Box sx={{ width: '100%' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Box display="flex" alignItems="center">
                            {getDirectionIcon(call.call_direction)}
                            <Typography variant="body2" ml={0.5}>
                              {call.call_direction}
                            </Typography>
                          </Box>
                          <Chip
                            icon={<TimerIcon />}
                            label={`${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}`}
                            size="small"
                            color="primary"
                          />
                        </Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {call.remote_number}
                        </Typography>
                        {call.caller_id_name && (
                          <Typography variant="caption" color="text.secondary">
                            {call.caller_id_name}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                    {index < activeCalls.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Direction</TableCell>
                      <TableCell>Remote Number</TableCell>
                      <TableCell>Caller ID</TableCell>
                      <TableCell>Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeCalls.map((call) => (
                      <TableRow key={call.id}>
                        <TableCell>
                          {getDirectionIcon(call.call_direction)}
                          {call.call_direction}
                        </TableCell>
                        <TableCell>{call.remote_number}</TableCell>
                        <TableCell>{call.caller_id_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            icon={<TimerIcon />}
                            label={`${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}`}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Call History Filters</Typography>
          
          {/* Date Range Quick Presets for Mobile */}
          {isMobile && (
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                <Chip
                  label="Today"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[UserCallHistory] Today button clicked');

                    // Clear any existing timer to prevent race conditions
                    if (dateChangeTimer.current) {
                      clearTimeout(dateChangeTimer.current);
                    }

                    const today = new Date();
                    setStartDate(startOfDay(today));
                    setEndDate(endOfDay(today));
                    setPage(0);
                  }}
                  color={
                    startDate && endDate &&
                    !isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) &&
                    format(startDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') &&
                    format(endDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                      ? 'primary'
                      : 'default'
                  }
                  variant={
                    startDate && endDate &&
                    !isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) &&
                    format(startDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') &&
                    format(endDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                      ? 'filled'
                      : 'outlined'
                  }
                />
                <Chip
                  label="Yesterday"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[UserCallHistory] Yesterday button clicked');
                    const token = safeGetItem(localStorage, 'access_token') || safeGetItem(sessionStorage, 'access_token');
                    console.log('[UserCallHistory] Current auth token:', token ? 'Present' : 'Missing');
                    
                    // Clear any existing timer to prevent race conditions
                    if (dateChangeTimer.current) {
                      clearTimeout(dateChangeTimer.current);
                    }
                    
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    setStartDate(startOfDay(yesterday));
                    setEndDate(endOfDay(yesterday));
                    setPage(0);
                  }}
                  variant="outlined"
                />
                <Chip
                  label="Last 7 Days"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[UserCallHistory] Last 7 Days button clicked');

                    // Clear any existing timer to prevent race conditions
                    if (dateChangeTimer.current) {
                      clearTimeout(dateChangeTimer.current);
                    }

                    const end = new Date();
                    const start = new Date();
                    start.setDate(start.getDate() - 6);
                    setStartDate(startOfDay(start));
                    setEndDate(endOfDay(end));
                    setPage(0);
                  }}
                  variant="outlined"
                />
                <Chip
                  label="Last 30 Days"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[UserCallHistory] Last 30 Days button clicked');

                    // Clear any existing timer to prevent race conditions
                    if (dateChangeTimer.current) {
                      clearTimeout(dateChangeTimer.current);
                    }

                    const end = new Date();
                    const start = new Date();
                    start.setDate(start.getDate() - 29);
                    setStartDate(startOfDay(start));
                    setEndDate(endOfDay(end));
                    setPage(0);
                  }}
                  variant="outlined"
                />
                <Chip
                  label="This Month"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[UserCallHistory] This Month button clicked');

                    // Clear any existing timer to prevent race conditions
                    if (dateChangeTimer.current) {
                      clearTimeout(dateChangeTimer.current);
                    }

                    const now = new Date();
                    const start = new Date(now.getFullYear(), now.getMonth(), 1);
                    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    setStartDate(startOfDay(start));
                    setEndDate(endOfDay(end));
                    setPage(0);
                  }}
                  variant="outlined"
                />
              </Stack>
            </Box>
          )}
          
          <Grid container spacing={isMobile ? 1 : 2} alignItems="center">
            {!isMobile ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={startDate && !isNaN(startDate.getTime()) ? format(startDate, 'yyyy-MM-dd') : ''}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={endDate && !isNaN(endDate.getTime()) ? format(endDate, 'yyyy-MM-dd') : ''}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      label="From"
                      type="date"
                      value={startDate && !isNaN(startDate.getTime()) ? format(startDate, 'yyyy-MM-dd') : ''}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <Typography variant="body2" sx={{ px: 1 }}>to</Typography>
                    <TextField
                      label="To"
                      type="date"
                      value={endDate && !isNaN(endDate.getTime()) ? format(endDate, 'yyyy-MM-dd') : ''}
                      onChange={(e) => handleEndDateChange(e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Direction</InputLabel>
                <Select
                  value={direction}
                  onChange={(e) => setDirection(e.target.value as any)}
                  label="Direction"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="inbound">Inbound</MenuItem>
                  <MenuItem value="outbound">Outbound</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Search Number"
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                fullWidth
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics */}
      {statistics && (
        <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>Total Calls</Typography>
                <Typography variant={isMobile ? "h5" : "h4"}>{statistics.total_calls}</Typography>
                <Box display="flex" justifyContent="space-between" mt={1} flexWrap="wrap" gap={0.5}>
                  <Chip
                    icon={isMobile ? undefined : <InboundIcon />}
                    label={`In: ${statistics.inbound_calls}`}
                    size="small"
                    color="primary"
                    sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}
                  />
                  <Chip
                    icon={isMobile ? undefined : <OutboundIcon />}
                    label={`Out: ${statistics.outbound_calls}`}
                    size="small"
                    color="secondary"
                    sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>Answer Rate</Typography>
                <Typography variant={isMobile ? "h5" : "h4"}>{statistics.answer_rate}%</Typography>
                <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap">
                  <Chip
                    icon={isMobile ? undefined : <AnsweredIcon />}
                    label={`${statistics.answered_calls}`}
                    size="small"
                    color="success"
                    sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}
                  />
                  <Chip
                    icon={isMobile ? undefined : <MissedIcon />}
                    label={`${statistics.missed_calls}`}
                    size="small"
                    color="warning"
                    sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>Total Duration</Typography>
                <Typography variant={isMobile ? "body2" : "body1"} fontWeight="medium" mb={1}>
                  {statistics.total_calls} Total - Tot: {statistics.total_duration_formatted} Avg: {statistics.avg_duration_formatted}
                </Typography>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" minWidth="80px">
                      <InboundIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">{statistics.inbound_calls} In</Typography>
                    </Box>
                    <Typography variant="caption" fontWeight="medium">Tot: {statistics.inbound_duration_formatted}</Typography>
                    <Typography variant="caption" color="textSecondary">Avg: {statistics.avg_inbound_duration_formatted}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" minWidth="80px">
                      <OutboundIcon color="secondary" fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">{statistics.outbound_calls} Out</Typography>
                    </Box>
                    <Typography variant="caption" fontWeight="medium">Tot: {statistics.outbound_duration_formatted}</Typography>
                    <Typography variant="caption" color="textSecondary">Avg: {statistics.avg_outbound_duration_formatted}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" minWidth="80px">
                      <InternalIcon color="info" fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">{statistics.internal_calls} Int</Typography>
                    </Box>
                    <Typography variant="caption" fontWeight="medium">Tot: {statistics.internal_duration_formatted}</Typography>
                    <Typography variant="caption" color="textSecondary">Avg: {statistics.avg_internal_duration_formatted}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>Unique Numbers</Typography>
                <Typography variant={isMobile ? "h5" : "h4"}>{statistics.unique_numbers}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Call History Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Your Call History</Typography>
            <IconButton onClick={fetchCallHistory} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Mobile Card View */}
              {isMobile ? (
                <>
                  <Stack spacing={1.5} sx={{ p: 0 }}>
                    {calls.map((call) => (
                      <Card key={`${call.id}-${call.start_time}`} sx={{ overflow: 'visible' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          {/* Header with Direction Badge */}
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                            <Box>
                              <Chip
                                icon={getDirectionIcon(call.direction)}
                                label={call.direction ? call.direction.charAt(0).toUpperCase() + call.direction.slice(1) : 'Unknown'}
                                size="small"
                                color={
                                  call.direction === 'inbound' ? 'primary' : 
                                  call.direction === 'outbound' ? 'secondary' : 
                                  'default'
                                }
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption" display="block" color="text.secondary">
                                {formatInUserTimezone(call.start_time, member?.timezone || 'America/Toronto')}
                              </Typography>
                            </Box>
                            <Chip
                              label={getDisplayDisposition(call.disposition, call.duration)}
                              size="small"
                              color={getDispositionColor(call.disposition, call.duration) as any}
                            />
                          </Box>
                          
                          {/* Dialed Number (if available) */}
                          {(call.incoming_did || call.phone_number) && (
                            <Box mb={1}>
                              <Typography variant="caption" color="text.secondary">
                                Dialed: {call.incoming_did || call.phone_number}
                              </Typography>
                            </Box>
                          )}

                          {/* Phone Number & Caller ID */}
                          <Box mb={1.5}>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                              {call.remote_number}
                            </Typography>
                            {call.caller_id_name && (
                              <Typography variant="body2" color="text.secondary">
                                {call.caller_id_name}
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Duration */}
                          <Box display="flex" alignItems="center" mb={call.recording ? 1.5 : 0}>
                            <TimerIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Duration: {call.duration_formatted}
                            </Typography>
                          </Box>
                          
                          {/* Recording Actions */}
                          {call.recording && (
                            <Box display="flex" gap={1}>
                              <Button
                                size="small"
                                variant={playingRecordingId === call.recording.id ? "contained" : "outlined"}
                                startIcon={playingRecordingId === call.recording.id ? <StopIcon /> : <PlayIcon />}
                                onClick={() => handlePlayRecording(call)}
                                sx={{ flex: 1 }}
                              >
                                {playingRecordingId === call.recording.id ? 'Stop' : 'Play'}
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadRecording(call)}
                                sx={{ 
                                  border: '1px solid', 
                                  borderColor: 'divider', 
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyRecordingLink(call)}
                                sx={{ 
                                  border: '1px solid', 
                                  borderColor: 'divider', 
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <ShareIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                  {/* Mobile Pagination */}
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      },
                      '.MuiTablePagination-select': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      }
                    }}
                  />
                </>
              ) : (
                /* Desktop Table View */
                <>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date/Time</TableCell>
                          <TableCell>Direction</TableCell>
                          <TableCell>Dialed Number</TableCell>
                          <TableCell>Remote Number</TableCell>
                          <TableCell>Caller ID</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Recording</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {calls.map((call, index) => (
                          <TableRow key={`${call.id}-${index}`}>
                            <TableCell>
                              {formatInUserTimezone(call.start_time, member?.timezone || 'America/Toronto')}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {getDirectionIcon(call.direction)}
                              <Typography variant="body2" ml={0.5}>
                                {call.direction || 'Unknown'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{call.incoming_did || call.phone_number || '-'}</TableCell>
                          <TableCell>{call.remote_number}</TableCell>
                          <TableCell>{call.caller_id_name || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={call.duration_formatted}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getDisplayDisposition(call.disposition, call.duration)}
                              size="small"
                              color={getDispositionColor(call.disposition, call.duration) as any}
                            />
                          </TableCell>
                          <TableCell>
                            {call.recording ? (
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip title={playingRecordingId === call.recording.id ? "Stop Recording" : "Play Recording"}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePlayRecording(call)}
                                    color="primary"
                                  >
                                    {playingRecordingId === call.recording.id ? (
                                      <StopIcon fontSize="small" />
                                    ) : (
                                      <PlayIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download Recording">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadRecording(call)}
                                    color="default"
                                  >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Recording Link">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyRecordingLink(call)}
                                  color="default"
                                >
                                  <ShareIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              No recording
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Audio player - visible on mobile, hidden on desktop */}
      {audioUrl && playingRecordingId && (
        <audio
          ref={audioRef}
          src={audioUrl}
          controls={isMobile}
          autoPlay
          crossOrigin="use-credentials"
          onLoadedMetadata={(e) => {
            const target = e.currentTarget;
            target.volume = 1.0;
            target.muted = false;
          }}
          onEnded={() => {
            console.log('Playback finished');
            setPlayingRecordingId(null);
            setAudioUrl(null);
          }}
          onError={(e) => {
            const target = e.currentTarget;
            console.error('Audio playback error:', {
              error: e,
              src: target.src,
              networkState: target.networkState,
              readyState: target.readyState,
              errorCode: target.error?.code,
              errorMessage: target.error?.message
            });
            setError('Failed to play recording');
            setPlayingRecordingId(null);
            setAudioUrl(null);
          }}
          style={isMobile ? {
            width: '100%',
            position: 'fixed',
            bottom: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            maxWidth: 'calc(90% - 32px)',
            backgroundColor: '#fff',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            borderRadius: '4px',
            padding: '4px'
          } : {
            display: 'none'
          }}
        />
      )}
    </Box>
  );
};

export default UserCallHistory;